import { effect, type ReadonlySignal, signal } from "@preact/signals-core";

type Pending = { mutationId: string; acked: boolean };
type Timer = ReturnType<typeof globalThis.setTimeout>;

// On once any change has waited `after` ms for the server's answer, each timed
// from when it was made, so a backlog stays on while its oldest one settles.
export function watchSaving({
	pending,
	after = 2000,
	now = Date.now,
	setTimeout = globalThis.setTimeout,
	clearTimeout = globalThis.clearTimeout,
}: {
	pending: ReadonlySignal<Pending[]>;
	after?: number;
	now?: () => number;
	setTimeout?: (fn: () => void, ms: number) => Timer;
	clearTimeout?: (timer: Timer) => void;
}) {
	const saving = signal(false);
	const madeAt = new Map<string, number>();
	let timer: Timer | null = null;

	const stop = effect(() => {
		const waiting = new Set(pending.value.filter((entry) => !entry.acked).map((entry) => entry.mutationId));
		const time = now();
		for (const id of madeAt.keys()) if (!waiting.has(id)) madeAt.delete(id);
		for (const id of waiting) if (!madeAt.has(id)) madeAt.set(id, time);
		if (timer) clearTimeout(timer);
		timer = null;
		const left = Math.min(...madeAt.values()) + after - time;
		saving.value = left <= 0;
		if (left > 0 && Number.isFinite(left)) {
			timer = setTimeout(() => {
				saving.value = true;
			}, left);
		}
	});

	return {
		saving,
		dispose() {
			stop();
			if (timer) clearTimeout(timer);
		},
	};
}
